import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'library/util/core-router';
import PathSvg from 'library/icons/path.svg';
import CaretSvg from 'library/icons/caret.svg';
import 'library/styles/global/rotate.scss';
import { resolve } from 'library/helpers/path-resolver';
import withData from 'util/hoc/with-data';
import useShowMorePages from 'util/hoc/useShowMorePages';
import { capitalize } from 'library/helpers/text';
import DownloadSvg from 'library/icons/download.svg';
import Search from 'library/components/form-elements/search';
import Loading from 'library/components/loading';

import 'apps/hiring/styles/components/survey-list.scss';
import 'apps/hiring/styles/components/users-list.scss';

const TAGS = {
  invited: 'Pending',
  complete: 'Completed',
  ideal: 'Part 1 done',
  real: 'Started'
};

const OpeningColumn = ({ label, value }) => (
  <span key={label} className={`user-list-${label}`}><Link to={`/hiring/openings/${value.id}`}>{value.name}</Link></span>
);

const ViewColumn = ({ label, value }) => (
  <span key={label} className={`user-list-${label}`}>
    <Link to={value}>
      <PathSvg />
    </Link>
  </span>
);

const DateColumn = ({ label, value }) => {
  const d = new Date(value);
  let date = '--/--/----';
  if (d != 'Invalid Date') {
    date = `${d.getMonth()+1}/${d.getDate()}/${d.getFullYear()}`;
  }

  return (
    <span key={label} className={`user-list-${label}`}>{date}</span>
  );
};

const SurveyStatusColumn = ({ label, value }) => (
  <span key={label} className={`user-list-${label}`}>
    <span className={['hiring-survey-row-tag', `tag-${value||'invited'}`].join(' ')}>
      { resolve(TAGS, value, 'Pending')}
    </span>
  </span>
);

const ResumeColumn = ({ label, value }) => (
  <span key={label} className={`user-list-${label}`}>
    <a key={value} target="_blank" rel="noopener noreferrer" href={value} disabled={!value}><DownloadSvg /></a>
  </span>
)

const COLUMN_COMPONENTS = {
  opening: OpeningColumn,
  view: ViewColumn,
  survey_status: SurveyStatusColumn,
  resume: ResumeColumn,
  date_applied: DateColumn,
  survey_date: DateColumn
};

const DefaultColumn = ({ label, value }) => (
  <span className={`user-list-${label}`}>{value}</span>
);

const UserList = (props) => {
  const [sort, setSort] = useState('');
  const [limit, setLimit] = useState(15);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    load();
  }, [limit]);

  const mapUserColumn = (key, value) => {
    const Component = resolve(COLUMN_COMPONENTS, key, DefaultColumn);

    return (
      <Component key={key} label={key} value={value} />
    );
  }

  const mapUsers = (user, i) => (
    <div key={i} className='user-list-row'>
      {
        Object.keys(user).map(k=>mapUserColumn(k, user[k]))
      }
    </div>
  );

  const sortIt = (column) => {
    let [key='',dir=''] = sort.split(':');
    if (column == key && dir == 'up') { dir = 'down' }
    else { dir = 'up' }
    setSort(`${column}:${dir}`);
  };

  const handleSort = (e) => {
    const column = e.target.dataset.name;
    if (!resolve(props, `sortFuncs/${column}`, false)) return;

    let [key='',dir=''] = sort.split(':');
    if (column == key && dir == 'up') {
      dir = 'down';
    }
    else {
      dir = 'up';
    }
    setSort(`${column}:${dir}`);
  };

  const doSort = (a, b) => {
    if (!sort) return 0;
    const [key='', dir=''] = sort.split(':');

    if (resolve(props, `sortFuncs/${key}`, false)) {
      return props.sortFuncs[key](sort,a,b);
    }

    const direction = dir==='down'?-1:1;

    let ret = 0;
    if (isNaN(resolve(a, key)) || isNaN(resolve(b, key))) {
      ret = `${resolve(a, key)}`.localeCompare(`${resolve(b, key)}`);
    } else {
      ret = resolve(a, key) - resolve(b, key);
    }

    return ret * direction;
  };

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await props.query({
        limit: limit,
        name: search
      });
      props.graph_update(data);
      setLoading(false);
    } catch (e) {
      console.error(e);
    }
  };

  const renderHeaderTotal = () => {
    const total = resolve(props, 'graph_data/users/pagination/total', 0);

    return (
      `(${resolve(props, 'graph_data/users/results', []).length} of ${total})`
    );
  }

  const renderShowMoreLess = () => {
    const total = resolve(props, 'graph_data/users/pagination/total', null);
    const count = Math.min(resolve(props, 'graph_data/users/results', []).length, limit);

    return useShowMorePages({
      count,
      loading: props.graph_loading || loading,
      total, setLimit,
      limit,
      pageSize: props.pageSize
    });
  };

  const mapHeaders = (header, i) => {
    const [key, dir] = sort.split(':');
    return (
      <span className={`user-list-${header}`} data-sortable={resolve(props, `sortFuncs/${header}`, false)} data-name={header} onClick={handleSort} disabled={is_loading}>
        { capitalize(header.replace(/[^a-zA-Z0-9]/g, ' '), true) }
        {
          (!key && i===0 || key===header) && resolve(props, `sortFuncs/${header}`, false)
            ? (
              <CaretSvg className={['flip-then-rotate-180', dir == 'up' ? null: 'active'].filter(c=>c).join(' ')}/>
            )
            : null
        }
      </span>
    );
  }

  let searchTimeout = null

  const throttledSearch = () => {
    clearTimeout(searchTimeout);

    searchTimeout = setTimeout(load, 350);
  }

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearch(value);
    throttledSearch();
  }

  const renderLoading = () => (
    is_loading()
      ? (
        <Loading width={'1em'} height={'1em'} />
      )
      : null
  );

  function is_loading() {
    return props.graph_loading||loading;
  }

  const keys = Object.keys(resolve(props.graph_data, 'users/results/0', {}));
  const [key, dir] = sort.split(':');

  return props.graph_loading ? null : (
    <div className='user-list-main'>
      <div className="user-list-label">
        <h2> {props.header} { renderHeaderTotal() } { renderLoading() } </h2>
        {
          props.showSearch
            ? (
              <Search disabled={is_loading} value={search} onChange={handleSearchChange} />
            )
            : null
        }
      </div>
      <div className='user-list-table' disabled={is_loading}>
        <div className='user-list-header'>
          { keys.map(mapHeaders) }
        </div>
        {
          resolve(props.graph_data, 'users/results', []).length
            ? (
              resolve(props.graph_data, 'users/results', []).sort(doSort).map(mapUsers)
            )
            : (
              <h2 className="user-list-error">No {props.header}</h2>
            )
        }
      </div>
      { renderShowMoreLess() }
    </div>
  );
};

UserList.defaultProps = {
  sortFuncs: {},
  showSearch: true,
  pageSize: 15
};

UserList.propTypes = {
  query: PropTypes.func.isRequired,
  sortFuncs: PropTypes.objectOf(PropTypes.func),
  parseQuery: PropTypes.func,
  header: PropTypes.string,
  graph_loading: PropTypes.bool.isRequired,
  graph_data: PropTypes.any,
  graph_update: PropTypes.func,
  showSearch: PropTypes.bool,
  pageSize: PropTypes.number
};

UserList.query = async (token, { query='', pageSize=15 }) => (
  await query({limit:pageSize})
);

UserList.parseQueryData = async (data, { parseQuery=(d)=>d }) => (
  await parseQuery(data)
);

export default withData(UserList);
